import React, { useEffect, useMemo, useState } from "react";
import { Button, Tooltip } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import AppTextField from "../../../lib/components/AppTextField";
import BTNLoader from "../../../lib/components/BTNLoader";
import NumberFormatCustom from "../../../lib/components/CurrencyFormat";
import marketplaceService from "../../../service/marketplaceService";
import { MARKETPLACE_ITEM_VALIDATION } from "../../../validations/marketplace";
import errorHandler from "../../../utils/errorHandler";
import pathnames from "../../../constants/pathnames";
import {
  BASE_PLAN_NAMES,
  filterBasePlans,
  getMonthlyAmount,
  isItemLocked,
  slugifyCode,
} from "../utils";
import CapabilityRulePicker from "./CapabilityRulePicker";

const QUANTITY_BOUND_HINTS = {
  Min: "The fewest units a customer can buy at once.",
  Max: "The most units a customer can buy at once.",
  Step:
    "The increment allowed between min and max. Set to 1 to let customers buy any whole number of units. Set higher to force bulk purchasing — e.g. step 3 only allows 3, 6, 9.",
};

const FieldLabelWithTip = ({ label, tip }) => (
  <label className="mp-small mp-label-with-tip">
    <span>{label}</span>
    <Tooltip title={tip} arrow placement="top" enterTouchDelay={0}>
      <span className="mp-info-tip-wrap" tabIndex={0} role="img" aria-label={`${label} info`}>
        <InfoOutlined className="mp-info-tip" fontSize="inherit" />
      </span>
    </Tooltip>
  </label>
);

const defaultValues = {
  rule: "",
  name: "",
  description: "",
  kind: "LIMIT",
  unitValue: "",
  unitLabel: "",
  quantityMin: 1,
  quantityMax: 10,
  quantityStep: 1,
  monthlyAmount: "",
  status: "ACTIVE",
  eligiblePlanIds: [],
  bundleRules: [],
};

function deriveIntervalPrices(monthly, discountRate = 0.1) {
  const m = Number(monthly) || 0;
  const d = Number(discountRate);
  const discount = Number.isFinite(d) ? d : 0.1;
  return [
    { label: "Monthly", months: 1, amount: Math.round(m) },
    { label: "Quarterly", months: 3, amount: Math.round(m * 3) },
    {
      label: "Biannually",
      months: 6,
      amount: Math.round(m * 6 * (1 - discount / 2)),
    },
    {
      label: "Annually",
      months: 12,
      amount: Math.round(m * 12 * (1 - discount)),
    },
  ];
}

/**
 * Create / edit marketplace item form (admin Part B2/B3).
 * Layout follows the marketplace "New item" mockup.
 */
const ItemForm = ({
  item = null,
  plans = [],
  meta = null,
  onSuccess,
  onCancel,
  mode = "create",
}) => {
  const navigate = useNavigate();
  const isEdit = mode === "edit" || Boolean(item?.id);
  const locked = isEdit && isItemLocked(item);
  const basePlans = useMemo(() => filterBasePlans(plans), [plans]);
  const [bundleOpen, setBundleOpen] = useState(false);

  const handleCancel = () => {
    if (onCancel) onCancel();
    else navigate(pathnames.MARKETPLACE_ADDONS);
  };

  const formHook = useForm({
    resolver: yupResolver(MARKETPLACE_ITEM_VALIDATION),
    mode: "all",
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = formHook;

  const ruleKey = useWatch({ control, name: "rule" });
  const kind = useWatch({ control, name: "kind" });
  const unitValue = useWatch({ control, name: "unitValue" });
  const monthlyAmount = useWatch({ control, name: "monthlyAmount" });
  const status = useWatch({ control, name: "status" });
  const eligiblePlanIds = useWatch({ control, name: "eligiblePlanIds" }) || [];
  const bundleRules = useWatch({ control, name: "bundleRules" }) || [];

  const rulesQuery = useQuery({
    retry: false,
    queryKey: ["marketplace-rules"],
    queryFn: () => marketplaceService.getRules(),
  });

  const rules = useMemo(() => rulesQuery.data || [], [rulesQuery.data]);
  const selectedRule = useMemo(
    () => rules.find((r) => r.rule === ruleKey) || null,
    [rules, ruleKey]
  );

  const discountRate = Number(meta?.discountRate ?? meta?.volumeDiscount ?? 0.1);

  /** Plans for eligibility UI — from selected rule, limited to the four base plans. */
  const eligibilityPlans = useMemo(() => {
    const fromRule = (selectedRule?.plans || [])
      .map((p) => ({
        id: String(p.id || p._id || ""),
        name: p.name,
        rank: p.rank ?? 0,
        granted: Boolean(p.granted),
        value: p.value,
        unlimited: p.unlimited,
      }))
      .filter((p) => BASE_PLAN_NAMES.some((n) => n.toLowerCase() === String(p.name).toLowerCase()));

    if (fromRule.length) {
      // Prefer matching ids from subscription plans list when available
      return fromRule.map((rp) => {
        const match = basePlans.find(
          (bp) => bp.name.toLowerCase() === String(rp.name).toLowerCase()
        );
        return { ...rp, id: match?.id || rp.id };
      });
    }

    return basePlans.map((p) => ({
      ...p,
      granted: false,
      value: null,
      unlimited: false,
    }));
  }, [selectedRule, basePlans]);

  const baselinePlanValue = useMemo(() => {
    const first = (selectedRule?.plans || []).find((p) => p.granted && !p.unlimited);
    if (first && first.value != null) return Number(first.value) || 0;
    return 20;
  }, [selectedRule]);

  useEffect(() => {
    if (item) {
      const rule =
        (Array.isArray(item.rules) && item.rules[0]) || item.rule || "";
      const bundle = Array.isArray(item.rules) ? item.rules.slice(1) : [];
      const excluded = new Set(
        (item.eligibility?.excludePlanIds || []).map(String)
      );

      reset({
        rule,
        name: item.name || "",
        description: item.description || "",
        kind: item.kind || "ACCESS",
        unitValue: item.unitValue != null && item.unitValue !== "" ? item.unitValue : "",
        unitLabel: item.unitLabel || "",
        quantityMin: item.quantity?.min ?? 1,
        quantityMax: item.quantity?.max ?? 10,
        quantityStep: item.quantity?.step ?? 1,
        monthlyAmount: getMonthlyAmount(item) || "",
        status: item.status === "HIDDEN" ? "HIDDEN" : "ACTIVE",
        eligiblePlanIds: basePlans
          .filter((p) => !excluded.has(String(p.id)))
          .map((p) => p.id),
        bundleRules: bundle,
      });
      if (bundle.length) setBundleOpen(true);
      return;
    }
    reset(defaultValues);
  }, [item, basePlans, reset]);

  // When rule is selected on create, seed kind/unitLabel and default eligible plans
  useEffect(() => {
    if (!selectedRule || (isEdit && locked)) return;
    setValue("kind", selectedRule.type || "ACCESS", { shouldValidate: true });
    if (selectedRule.unit) {
      setValue("unitLabel", selectedRule.unit, { shouldValidate: true });
    }
    if (!isEdit) {
      const buyable = eligibilityPlans
        .filter((p) => !p.granted && p.id)
        .map((p) => p.id);
      setValue("eligiblePlanIds", buyable, { shouldValidate: true });
    }
  }, [selectedRule, isEdit, locked, setValue, eligibilityPlans]);

  const priceRows = useMemo(
    () => deriveIntervalPrices(monthlyAmount, discountRate),
    [monthlyAmount, discountRate]
  );

  const isLimit = String(kind).toUpperCase() === "LIMIT";

  const unitValueTip = useMemo(() => {
    const exampleTotal = baselinePlanValue + (Number(unitValue) || 0) * 2;
    const unitSuffix = selectedRule?.unit ? ` ${selectedRule.unit}` : "";
    return (
      <div className="mp-tip-body">
        <p>
          What one purchase grants. Set to 1 to sell one seat at a time, or higher to
          sell in packs.
        </p>
        <p>
          A customer with <strong>{baselinePlanValue}</strong> today buying quantity 2
          gets <strong>{exampleTotal}</strong>
          {unitSuffix}.
        </p>
      </div>
    );
  }, [baselinePlanValue, unitValue, selectedRule?.unit]);

  const handleWholeNumberChange = (onChange) => (e) => {
    const raw = e.target.value;
    if (raw === "" || /^\d*$/.test(raw)) {
      onChange(raw === "" ? "" : Number(raw));
    }
  };

  const mutation = useMutation({
    mutationFn: async (values) => {
      const allPlanIds = eligibilityPlans.map((p) => p.id).filter(Boolean);
      // Always exclude plans that already grant this capability
      const grantedIds = eligibilityPlans
        .filter((p) => p.granted)
        .map((p) => p.id)
        .filter(Boolean);
      const excludePlanIds = [
        ...new Set([
          ...grantedIds,
          ...allPlanIds.filter((id) => !values.eligiblePlanIds.includes(id)),
          ...basePlans
            .map((p) => p.id)
            .filter((id) => !values.eligiblePlanIds.includes(id) && !allPlanIds.includes(id)),
        ]),
      ];

      const rulesPayload = [values.rule, ...(values.bundleRules || [])].filter(
        Boolean
      );

      const payload = {
        code: slugifyCode(values.name),
        name: values.name,
        description: values.description,
        kind: values.kind,
        rules: rulesPayload,
        monthlyAmount: Number(values.monthlyAmount),
        eligibility: { excludePlanIds },
        status: values.status,
      };

      if (String(values.kind).toUpperCase() === "LIMIT") {
        payload.unitValue = Number(values.unitValue);
        payload.unitLabel = values.unitLabel || selectedRule?.unit || "unit";
        payload.quantity = {
          min: Number(values.quantityMin),
          max: Number(values.quantityMax),
          step: Number(values.quantityStep),
        };
      }

      // Edit uses the same body shape as create.
      if (isEdit) {
        return marketplaceService.updateItem(item.id, payload);
      }

      return marketplaceService.createItem(payload);
    },
    onError: ({ response }) => errorHandler(response),
    onSuccess: () => {
      toast.success(isEdit ? "Item updated" : "Item published");
      onSuccess?.();
      if (!isEdit) navigate(pathnames.MARKETPLACE_ADDONS);
    },
  });

  const submitWithStatus = (nextStatus) => {
    setValue("status", nextStatus, { shouldValidate: true });
    handleSubmit((values) =>
      mutation.mutate({ ...values, status: nextStatus })
    )();
  };

  const toggleEligible = (planId) => {
    const next = eligiblePlanIds.includes(planId)
      ? eligiblePlanIds.filter((id) => id !== planId)
      : [...eligiblePlanIds, planId];
    setValue("eligiblePlanIds", next, { shouldValidate: true });
  };

  const bundleOptions = useMemo(
    () => rules.filter((r) => r.rule !== ruleKey),
    [rules, ruleKey]
  );

  return (
    <form
      className="mp-item-form"
      onSubmit={handleSubmit((values) => mutation.mutate(values))}
    >
      {/* Capability */}
      <div className="mp-field-row">
        <div className="mp-field-label">
          <div className="name">Capability</div>
          <span className="hint">
            What this item grants or raises. Everything below follows from this.
          </span>
        </div>
        <div className="mp-field-body">
          <Controller
            name="rule"
            control={control}
            render={({ field }) => (
              <CapabilityRulePicker
                rules={rules}
                value={field.value || null}
                disabled={(isEdit && locked) || rulesQuery.isFetching}
                error={errors.rule}
                helperText={errors.rule?.message}
                onChange={(rule) => {
                  field.onChange(rule?.rule || "");
                  if (rule && !isEdit) {
                    setValue("kind", rule.type || "ACCESS");
                    if (rule.unit) setValue("unitLabel", rule.unit);
                  }
                }}
              />
            )}
          />
        </div>
      </div>

      {/* Name & description */}
      <div className="mp-field-row">
        <div className="mp-field-label">
          <div className="name">Name &amp; description</div>
          <span className="hint">Shown to customers in the catalogue.</span>
        </div>
        <div className="mp-field-body">
          <label className="mp-small">Name</label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <AppTextField
                {...field}
                placeholder="500 extra invoices a month"
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />

          <label className="mp-small" style={{ marginTop: 14 }}>
            Description
          </label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <AppTextField
                {...field}
                multiline
                minRows={2}
                placeholder="Send 500 more invoices each month, at a lower rate per invoice."
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            )}
          />
        </div>
      </div>

      {/* Capacity / module */}
      <div className="mp-field-row">
        <div className="mp-field-label">
          <div className="name">
            {isLimit ? "How much one purchase is worth" : "Module settings"}
          </div>
          <span className="hint">
            {isLimit
              ? "How much capacity each unit of this item adds."
              : "Modules have no quantity — they're either granted or not."}
          </span>
        </div>
        <div className="mp-field-body">
          {isLimit ? (
            <>
              <FieldLabelWithTip label="Value per unit" tip={unitValueTip} />
              <Controller
                name="unitValue"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    value={field.value ?? ""}
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g. 100"
                    disabled={isEdit && locked}
                    error={!!errors.unitValue}
                    helperText={errors.unitValue?.message}
                    onChange={handleWholeNumberChange(field.onChange)}
                  />
                )}
              />
            </>
          ) : (
            <div className="mp-callout info">
              <span className="dot" />
              <span>
                This is an on/off unlock — no quantity, no unit value. A customer
                either has it or doesn&apos;t.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Quantity bounds */}
      {isLimit ? (
        <div className="mp-field-row">
          <div className="mp-field-label">
            <div className="name">Quantity bounds</div>
            <span className="hint">How many units a customer can buy at once.</span>
          </div>
          <div className="mp-field-body">
            <div className="mp-grid3">
              <div>
                <FieldLabelWithTip label="Min" tip={QUANTITY_BOUND_HINTS.Min} />
                <Controller
                  name="quantityMin"
                  control={control}
                  render={({ field }) => (
                    <AppTextField
                      {...field}
                      value={field.value ?? ""}
                      type="text"
                      inputMode="numeric"
                      placeholder="1"
                      error={!!errors.quantityMin}
                      helperText={errors.quantityMin?.message}
                      onChange={handleWholeNumberChange(field.onChange)}
                    />
                  )}
                />
              </div>
              <div>
                <FieldLabelWithTip label="Max" tip={QUANTITY_BOUND_HINTS.Max} />
                <Controller
                  name="quantityMax"
                  control={control}
                  render={({ field }) => (
                    <AppTextField
                      {...field}
                      value={field.value ?? ""}
                      type="text"
                      inputMode="numeric"
                      placeholder="10"
                      error={!!errors.quantityMax}
                      helperText={errors.quantityMax?.message}
                      onChange={handleWholeNumberChange(field.onChange)}
                    />
                  )}
                />
              </div>
              <div>
                <FieldLabelWithTip label="Step" tip={QUANTITY_BOUND_HINTS.Step} />
                <Controller
                  name="quantityStep"
                  control={control}
                  render={({ field }) => (
                    <AppTextField
                      {...field}
                      value={field.value ?? ""}
                      type="text"
                      inputMode="numeric"
                      placeholder="1"
                      error={!!errors.quantityStep}
                      helperText={errors.quantityStep?.message}
                      onChange={handleWholeNumberChange(field.onChange)}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Pricing */}
      <div className="mp-field-row">
        <div className="mp-field-label">
          <div className="name">Pricing</div>
          <span className="hint">
            Quarterly, biannual and annual prices are derived automatically.
          </span>
        </div>
        <div className="mp-field-body">
          <label className="mp-small">Price</label>
          <Controller
            name="monthlyAmount"
            control={control}
            render={({ field }) => (
              <AppTextField
                {...field}
                placeholder="20000"
                error={!!errors.monthlyAmount}
                helperText={errors.monthlyAmount?.message}
                InputProps={{
                  inputComponent: NumberFormatCustom,
                  startAdornment: (
                    <span className="text-secondary me-1 ps-2">₦</span>
                  ),
                }}
              />
            )}
          />
          <table className="mp-price-table">
            <thead>
              <tr>
                <th>Interval</th>
                <th>Price</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {priceRows.map((row) => {
                const uv = Number(unitValue) || 1;
                const rate =
                  isLimit && uv > 0
                    ? `₦${(row.amount / uv).toFixed(1)} per unit`
                    : "";
                return (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td className="mono">₦{row.amount.toLocaleString()}</td>
                    <td>
                      {rate ? <span className="rate">{rate}</span> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Eligibility */}
      <div className="mp-field-row">
        <div className="mp-field-label">
          <div className="name">Eligibility</div>
          <span className="hint">
            Which plans can buy this. Plans that already include it can&apos;t be
            picked.
          </span>
        </div>
        <div className="mp-field-body">
          <div className="mp-plan-list">
            {eligibilityPlans.map((plan) => {
              const checked = plan.granted || eligiblePlanIds.includes(plan.id);
              return (
                <label
                  key={plan.id || plan.name}
                  className={`mp-plan-row ${plan.granted ? "disabled" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={plan.granted}
                    onChange={() => !plan.granted && toggleEligible(plan.id)}
                  />
                  <span className="pname">{plan.name}</span>
                  <span className="prank">rank {plan.rank || "—"}</span>
                  {plan.granted ? (
                    <span className="granted-note">
                      already includes this — locked
                    </span>
                  ) : null}
                </label>
              );
            })}
          </div>
          {errors.eligiblePlanIds ? (
            <div className="text-danger fs-xs mt-1">
              {errors.eligiblePlanIds.message}
            </div>
          ) : null}
        </div>
      </div>

      {/* Bundle */}
      <div className="mp-field-row">
        <div className="mp-field-label">
          <div className="name">Bundle</div>
          <span className="hint">
            Optional — sell more than one capability as a single item.
          </span>
        </div>
        <div className="mp-field-body">
          {!bundleOpen ? (
            <button
              type="button"
              className="mp-add-rule-link"
              disabled={isEdit && locked}
              onClick={() => setBundleOpen(true)}
            >
              + Add another capability
            </button>
          ) : (
            <div>
              <label className="mp-small">Additional capability</label>
              <CapabilityRulePicker
                rules={bundleOptions}
                value={bundleRules[0] || null}
                disabled={isEdit && locked}
                onChange={(rule) => {
                  setValue("bundleRules", rule?.rule ? [rule.rule] : [], {
                    shouldValidate: true,
                  });
                }}
              />
              <button
                type="button"
                className="mp-link-btn mt-2"
                onClick={() => {
                  setBundleOpen(false);
                  setValue("bundleRules", []);
                }}
              >
                Remove bundle
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Status */}
      <div className="mp-field-row">
        <div className="mp-field-label">
          <div className="name">Status</div>
          <span className="hint">Retiring an item happens later, once it&apos;s live.</span>
        </div>
        <div className="mp-field-body">
          <div className="mp-status-choice">
            {[
              {
                value: "ACTIVE",
                name: "Active",
                desc: "Sells now and renews for existing holders.",
              },
              {
                value: "HIDDEN",
                name: "Hidden",
                desc: "No new sales, but current holders keep renewing.",
              },
            ].map((opt) => (
              <button
                type="button"
                key={opt.value}
                className={`mp-status-opt ${status === opt.value ? "selected" : ""}`}
                onClick={() => setValue("status", opt.value, { shouldValidate: true })}
              >
                <div className="sname">{opt.name}</div>
                <div className="sdesc">{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mp-footer-bar">
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          disabled={mutation.isPending}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        {!isEdit ? (
          <Button
            type="button"
            variant="outlined"
            disabled={mutation.isPending}
            startIcon={<BTNLoader isLoading={mutation.isPending} />}
            onClick={() => submitWithStatus("HIDDEN")}
          >
            Save as hidden
          </Button>
        ) : null}
        <Button
          type="button"
          variant="contained"
          disabled={mutation.isPending}
          startIcon={<BTNLoader isLoading={mutation.isPending} />}
          onClick={() => submitWithStatus(isEdit ? status || "ACTIVE" : "ACTIVE")}
        >
          {isEdit ? "Save changes" : "Publish item"}
        </Button>
      </div>
    </form>
  );
};

export default ItemForm;
